import { NgClass } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { forkJoin } from 'rxjs';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import {
  FILE_OBJECT_TYPE_INFO_REQUEST_COMMUNICATION_BACKOFFICE
} from 'types/information-request.interface';
import type {
  BackofficeInformationRequestCard,
  BackofficeInformationRequestCommunicationCard
} from 'types/information-request.interface';
import { getControlsWithErrors, MAX_LENGTH_ADDITIONAL_INFO } from 'util/form.utility';
import { BaseFormModalComponent } from '../base-form-modal.component';

@Component({
  selector: 'app-ir-send-email-modal',
  standalone: true,
  imports: [TranslateModule, SpinnerComponent, NgClass, TooltipComponent, ReactiveFormsModule, FileUploadComponent],
  templateUrl: './ir-send-email-modal.component.html',
  styleUrls: ['./ir-send-email-modal.component.scss', '../../form/base-form.scss']
})
export class IrSendEmailModalComponent extends BaseFormModalComponent implements AfterViewInit, OnChanges {
  public readonly maxFieldLength = MAX_LENGTH_ADDITIONAL_INFO;
  @Input() isOpen!: boolean;
  @Input() informationRequest!: BackofficeInformationRequestCard;
  @Output() update: EventEmitter<BackofficeInformationRequestCommunicationCard> =
    new EventEmitter<BackofficeInformationRequestCommunicationCard>();
  @ViewChild('content') content!: TemplateRef<any>;
  isCrudLoading = false;
  shouldRenderTooltip = false;
  readonly communicationFileType = FILE_OBJECT_TYPE_INFO_REQUEST_COMMUNICATION_BACKOFFICE;

  get backofficeText(): AbstractControl<any, any> | null {
    return this.form.get('backofficeText');
  }

  constructor(
    formBuilder: FormBuilder,
    config: NgbModalConfig,
    modalService: NgbModal,
    toastService: ToastService,
    translateService: TranslateService,
    private apiService: ApiService
  ) {
    super(formBuilder, config, modalService, toastService, translateService);
  }

  ngAfterViewInit(): void {
    if (this.isOpen) {
      this.fillForm();
      this.open(this.content);
    }
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.fillForm();
      this.shouldRenderTooltip = false;
      this.open(this.content);
      setTimeout(() => {
        this.shouldRenderTooltip = true;
      }, 10);
    }
  }

  fillForm(): void {
    const req = this.informationRequest;
    const isOrganization = !!req?.legalEntity;
    const clientEmail = isOrganization
      ? (req?.contactPersonEmail || req?.legalEntity?.email)
      : req?.client?.email;
    const code = req?.code || '';
    const topicName = req?.topicVersion?.name || '';
    const responsibleUnit = req?.responsibleStructuralUnitName || '';
    const defaultSubject = `Informācija par pieprasījumu Nr. ${code}, Latvijas Nacionālais arhīvs`;

    this.form = this.formBuilder.group({
      email: [clientEmail || '', [Validators.required]],
      emailSubject: [defaultSubject, [Validators.required]],
      backofficeText: ['', [Validators.required, Validators.maxLength(MAX_LENGTH_ADDITIONAL_INFO)]]
    });
    this.pendingFiles = [];

    const unit = req?.responsibleStructuralUnit;
    const unitInfo = unit
      ? this.buildUnitContactInfo(unit.name, unit.phone, unit.email)
      : responsibleUnit;
    this.patchDefaultBody(code, topicName, unitInfo);
  }

  private buildUnitContactInfo(name: string, phone?: string | null, email?: string | null): string {
    const parts = [name];
    if (phone) parts.push(phone);
    if (email) parts.push(email);
    return parts.join(', ');
  }

  private patchDefaultBody(code: string, topicName: string, unitInfo: string): void {
    const defaultBody = `Lūdzam sniegt papildu informāciju par Jūsu pieprasījumu Nr. ${code}, pieprasījuma tēma "${topicName}". Cieņā\nLatvijas Nacionālais arhīvs\n${unitInfo}\nŠis ir automātiski ģenerēts ziņojums, lūdzam neatbildēt.`;
    this.form.patchValue({ backofficeText: defaultBody });
  }

  protected override close(): void {
    this.pendingFiles = [];
    super.close();
  }

  private pendingFiles: File[] = [];

  onFilesChanged(event: { files: File[] }): void {
    this.pendingFiles = event.files;
  }

  submit(): void {
    if (this.form.valid) {
      this.isCrudLoading = true;

      if (this.pendingFiles.length === 0) {
        this.sendEmail([]);
        return;
      }

      const uploadObservables = this.pendingFiles.map((file) =>
        this.apiService.uploadFile(
          file,
          this.communicationFileType,
          this.informationRequest.id
        )
      );

      forkJoin(uploadObservables).subscribe({
        next: (fileIds: string[]) => {
          this.sendEmail(fileIds);
        },
        error: () => {
          this.toastService.showRequestError();
          this.isCrudLoading = false;
        }
      });
    } else {
      this.submitAttempted = true;
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'components.sendEmailMessage');
    }
  }

  private sendEmail(fileIds: string[]): void {
    this.apiService.sendIrByEmail({
      informationRequestId: this.informationRequest.id,
      recipient: this.form.value.email,
      emailSubject: this.form.value.emailSubject,
      emailBody: this.form.value.backofficeText,
      backofficeText: this.form.value.backofficeText,
      fileIds
    }).subscribe({
      next: (response) => {
        this.toastService.showSuccess('sendEmailMessageSuccess', '', {
          shouldTranslate: true
        });
        this.isCrudLoading = false;
        this.close();
        this.update.emit(response);
      },
      error: () => {
        this.toastService.showRequestError();
        this.isCrudLoading = false;
        this.close();
      }
    });
  }
}
